/**
 * @file useLobby.ts
 * @description
 * Custom React hook for managing the game lobby in real time.
 *
 * - Fetches initial waiting/started games via REST API on mount.
 * - Subscribes to socket events for real-time lobby updates.
 * - Exposes functions to create new games (via REST) and join existing ones (via Socket).
 * - Handles joining the game room via socket after creation/join.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { GameState } from './useGameState'; // Import GameState interface from useGameState

export interface GameSummary {
  code: string;
  playersCount: number;
  status: 'waiting' | 'started';
  spectatorsCount?: number; // Add spectator count for started games list
}

/**
 * Hook return type.
 */
interface UseLobbyReturn {
  waitingGames: GameSummary[];
  startedGames: GameSummary[];
  createGame: (isPublic?: boolean) => Promise<string>; // Returns the game code
  joinGame: (code: string) => Promise<void>; // Navigating happens after successful join
  isLoadingInitial: boolean; // Indicate if initial REST fetch is loading
  error: Error | null; // Any error during initial fetch
  socketError: Error | null; // Error from the socket connection
  refetchGames: () => Promise<void>; // Function to manually refetch games
}

/**
 * useLobby
 * Manages lobby state and actions via REST + Socket.io.
 */
const useLobby = (): UseLobbyReturn => {
  const { socket, isConnected, error: socketError } = useSocket(); // Get socket and its connection status/error
  const { playerName, isAuthenticated } = useAuth(); // Get player name and auth state
  const [waitingGames, setWaitingGames] = useState<GameSummary[]>([]);
  const [startedGames, setStartedGames] = useState<GameSummary[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [error, setError] = useState<Error | null>(null); // Error specifically for the initial fetch


  /**
   * Fetch initial lobby state via REST API on mount or when called manually.
   * This ensures the lobby page always displays the current games list.
   * This function now runs regardless of `isAuthenticated` being null or true.
   * It clears the lists if `isAuthenticated` is false.
   */
  const loadInitialGames = useCallback(async () => {
     console.log('loadInitialGames: Starting fetch. isAuthenticated:', isAuthenticated);
     // If not authenticated (check finished), clear lists and stop.
     if (isAuthenticated === false) {
         console.log('loadInitialGames: User is not authenticated, clearing game lists.');
         setIsLoadingInitial(false);
         setWaitingGames([]); // Clear lists if not authenticated
         setStartedGames([]);
         setError(null); // Clear any previous errors if unauthenticated
         return;
     }

     // If isAuthenticated is null (checking) or true (authenticated), proceed with fetch
     setIsLoadingInitial(true);
     setError(null); // Clear previous fetch errors
     console.log('loadInitialGames: Fetching initial lobby games via REST...');
    try {
      // Fetch waiting games
      const waitRes = await fetch('/api/games?status=waiting');
      if (waitRes.ok) {
        const waitData: any[] = await waitRes.json();
        console.log(`loadInitialGames: Fetched ${waitData.length} waiting games.`);
        waitData.forEach(g => {
          console.log(`loadInitialGames: Waiting game ${g.code} has ${g.players?.length} players.`);
          console.log(`loadInitialGames: Waiting game ${g.code} has structure ${JSON.stringify(g)}`);
        });
        setWaitingGames(
          waitData.map(g => ({
            code: g.code,
            playersCount: g.players?.length || g.playersCount || 0, // Ensure players count is included
            status: 'waiting',
          }))
        );
      } else {
         const errorText = await waitRes.text();
        console.error('loadInitialGames: Failed to fetch waiting games:', waitRes.status, errorText);
        // Do not clear games on fetch error, keep the previous state if possible
        // Set a specific error related to the fetch
         setError(new Error(`Failed to fetch waiting games: ${waitRes.status} ${errorText}`));
      }

      // Fetch started games
      const startRes = await fetch('/api/games?status=started');
      if (startRes.ok) {
        const startData: any[] = await startRes.json();
         console.log(`loadInitialGames: Fetched ${startData.length} started games.`);
         console.log(`loadInitialGames: Fetched ${startData.length} started games.`);
         startData.forEach(g => {
           console.log(`loadInitialGames: Started game ${g.code} has ${g.players?.length} players.`);
           console.log(`loadInitialGames: Started game ${g.code} has structure ${JSON.stringify(g)}`);
         });
        setStartedGames(
          startData.map(g => ({
            code: g.code,
            playersCount: g.players?.length || g.playersCount || 0, // Players in started games
            spectatorsCount: g.spectators?.length || 0, // Spectators in started games
            status: 'started',
          }))
        );
      } else {
         const errorText = await startRes.text();
        console.error('loadInitialGames: Failed to fetch started games:', startRes.status, errorText);
         // Append to existing error or create a new one
         setError(prevErr => new Error(`${prevErr ? prevErr.message + '; ' : ''}Failed to fetch started games: ${startRes.status} ${errorText}`));
      }
    } catch (err) {
      console.error('loadInitialGames: Error loading initial lobby state:', err);
      setError(prevErr => new Error(`${prevErr ? prevErr.message + '; ' : ''}Error loading lobby: ${(err as Error).message}`));
    } finally {
        setIsLoadingInitial(false);
        console.log('loadInitialGames: Fetching complete.');
    }
    // The dependency array includes isAuthenticated because the behavior of the function
    // changes based on its value (clear vs fetch).
    // However, the EFFECT that CALLS this function should not depend on isAuthenticated
    // if we want it to run on mount regardless of the initial null state.
  }, [isAuthenticated]); // Keep isAuthenticated dependency because the logic inside depends on it


  // Initial load effect
  useEffect(() => {
      console.log('useLobby Effect: Running initial load.');
      // This effect should trigger loadInitialGames on mount.
      // It should also trigger if the 'refetchGames' function identity changes,
      // but since refetchGames = loadInitialGames (useCallback), it only changes
      // if its dependencies change (isAuthenticated).
      // This means it WILL re-run when isAuthenticated changes from null to true,
      // which is exactly what we want with the new logic inside loadInitialGames.
      loadInitialGames();
  }, [loadInitialGames]); // Effect depends on the memoized callback


  // Expose loadInitialGames as refetchGames
  const refetchGames = loadInitialGames;


  /**
   * Subscribe to real-time socket events for lobby updates.
   * This effect re-runs if `socket` changes or connects.
   */
  useEffect(() => {
    if (!socket || !isConnected) {
        console.log('Socket not connected, skipping lobby event subscriptions.');
        // Clear game lists if socket disconnects? Or rely on refresh button?
        // Let's keep the last known state but show connection status.
        // If isAuthenticated is false, checkAuth in AuthContext clears things.
        return;
    };

    console.log('Subscribing to lobby socket events...');

    /** New game created (via REST or another client) */
    const onGameCreated = (gameSummary: { code: string; status: 'waiting'; players: { id: string; name: string }[] }) => {
       console.log('Socket event: lobbyGameCreated', gameSummary);
       // Add to waitingGames if it's not already there (deduplication needed)
       setWaitingGames(prev => {
            if (prev.some(g => g.code === gameSummary.code)) {
                 console.log(`Socket event: lobbyGameCreated for ${gameSummary.code} already in list, skipping.`);
                 return prev; // Already in list
            }
           console.log(`Socket event: lobbyGameCreated for ${gameSummary.code}. Adding to list.`);
           return [
             { code: gameSummary.code, playersCount: gameSummary.players.length, status: 'waiting' },
             ...prev, // Add to the beginning of the list
           ];
       });
        // Remove from startedGames if it somehow existed there (e.g., status change back - unlikely, but defensive)
        setStartedGames(prev => prev.filter(g => g.code !== gameSummary.code));
    };

    /** Player count or spectator count updated in a game */
    const onGameUpdated = (update: { code: string; playersCount?: number; spectatorsCount?: number }) => {
       console.log('Socket event: lobbyGameUpdated', update);
       let updated = false;
       // Check if the game is in the waiting list
       setWaitingGames(prev =>
         prev.map(g => {
           if (g.code === update.code) {
                updated = true; // Mark that we found/updated it
                // Only update if update has the specific count
                const updatedGame = { ...g };
                if (update.playersCount !== undefined) {
                    updatedGame.playersCount = update.playersCount;
                }
                 // Spectator count is not relevant for waiting games in the list view, don't update it here
                return updatedGame;
           }
           return g;
         })
       );
       // Check if the game is in the started list (for spectator updates or player count changes after game started)
       setStartedGames(prev =>
           prev.map(g => {
                if (g.code === update.code) {
                    updated = true; // Mark that we found/updated it
                   const updatedGame = { ...g };
                   // Only update if update has the specific count
                   if (update.playersCount !== undefined) {
                       updatedGame.playersCount = update.playersCount;
                   }
                   if (update.spectatorsCount !== undefined) {
                       updatedGame.spectatorsCount = update.spectatorsCount;
                   }
                   return updatedGame;
                }
               return g;
           })
       );

        // If the game wasn't found in either list, it might be a started game
        // that wasn't in the initial fetch (e.g., started just after fetch but before socket connect)
        // Or it might be a private game being updated.
        // For simplicity, if not found, trigger a refetch of all games.
        // A more complex approach would be to handle adding partial summaries here.
        // Only refetch if we are not currently loading initial games to prevent loops.
        if (!updated && !isLoadingInitial) {
            console.warn(`Received update for unknown game code ${update.code}. Refetching lobby lists.`);
            refetchGames(); // Manually refetch all games
        }

    };

    /** A game moved from waiting to started */
    const onGameStarted = (gameSummary: { code: string }) => { // Backend sends { code } for now
       console.log('Socket event: lobbyGameStarted', gameSummary);
       // Remove from waiting list
       setWaitingGames(prev => {
           const filtered = prev.filter(g => g.code !== gameSummary.code);
            console.log(`Socket event: lobbyGameStarted for ${gameSummary.code}. Removing from waiting list.`);
           return filtered;
       });

       // Add to started lists (minimal entry, refetch will get details)
       setStartedGames(prev => {
            if (prev.some(g => g.code === gameSummary.code)) {
                console.log(`Socket event: lobbyGameStarted for ${gameSummary.code} already in started list, skipping.`);
                 return prev; // Already in list
            }
           console.log(`Socket event: lobbyGameStarted for ${gameSummary.code}. Adding minimal entry to started list.`);
           // If we had playersCount and spectatorsCount from backend, we'd use them here.
           // For now, add a minimal entry. A full refetch below will correct this.
           return [
               { code: gameSummary.code, playersCount: 0, spectatorsCount: 0, status: 'started' },
               ...prev,
            ];
       });

        // Trigger a full refetch to get accurate player/spectator counts for the newly started game
        // Only refetch if not already loading to prevent loop
        if (!isLoadingInitial) {
            console.log('Socket event: lobbyGameStarted - Triggering refetchGames.');
             refetchGames();
        } else {
            console.log('Socket event: lobbyGameStarted - Skipping refetchGames as isLoadingInitial is true.');
        }
    };

    /** A game ended (win, loss, host end, no players) */
     const onGameEnded = (gameSummary: { code: string }) => {
         console.log('Socket event: lobbyGameEnded', gameSummary);
         // Remove from both waiting and started lists
         setWaitingGames(prev => {
             const filtered = prev.filter(g => g.code !== gameSummary.code);
              if (filtered.length < prev.length) console.log(`Socket event: lobbyGameEnded for ${gameSummary.code}. Removed from waiting list.`);
             return filtered;
         });
         setStartedGames(prev => {
             const filtered = prev.filter(g => g.code !== gameSummary.code);
             if (filtered.length < prev.length) console.log(`Socket event: lobbyGameEnded for ${gameSummary.code}. Removed from started list.`);
             return filtered;
         });
     };


    socket.on('lobbyGameCreated', onGameCreated);
    socket.on('lobbyGameUpdated', onGameUpdated);
    socket.on('lobbyGameStarted', onGameStarted);
    socket.on('lobbyGameEnded', onGameEnded); // Subscribe to new event

    return () => {
       console.log('Unsubscribing from lobby socket events...');
       if(socket) { // Check socket exists before off
           socket.off('lobbyGameCreated', onGameCreated);
           socket.off('lobbyGameUpdated', onGameUpdated);
           socket.off('lobbyGameStarted', onGameStarted);
           socket.off('lobbyGameEnded', onGameEnded);
       }
    };
  }, [socket, isConnected, isLoadingInitial, refetchGames]); // Re-run effect if socket, connection, isLoadingInitial, or refetchGames changes.

  /**
   * joinGame
   * Emits `joinGame` to the server with the given code and player name.
   * Resolves on successful acknowledgement. Navigating should happen after this resolves.
   */
  const joinGame = useCallback(
    (code: string): Promise<void> => // Returns void, caller navigates
      new Promise((resolve, reject) => {
        if (!socket || !isConnected) {
           console.warn('Cannot join game: socket not available or not connected.');
           return reject('Socket not connected. Please wait or refresh.');
        }
        if (playerName === null) { // Check explicitly for null
            console.warn('Cannot join game: Player name is missing.');
             return reject('Player name is required to join a game.'); // Should be handled by AuthContext/Login
        }
         console.log(`Attempting to join game ${code} via socket...`);

        // Set a timeout for the acknowledgement
        const ackTimeout = setTimeout(() => {
             console.error(`Socket joinGame acknowledgement timed out for game ${code}.`);
            reject('Server acknowledgement timed out.');
        }, 10000); // 10 seconds timeout

        socket.emit(
          'joinGame',
          { code, name: playerName }, // Send player name via socket event
          (resp: { success?: boolean; error?: string; state?: GameState; isSpectator?: boolean }) => {
             clearTimeout(ackTimeout); // Clear the timeout on receiving acknowledgement
             // The acknowledgement callback is received directly by the socket that emitted
            if (resp.error) {
               console.error(`Socket joinGame failed for game ${code}:`, resp.error);
               return reject(resp.error);
            }
             console.log(`Socket joinGame successful for game ${code}. Received initial state.`);
            // The state is received here (resp.state), but it's primarily managed by
            // the useGameState hook for the specific game room, which subscribes
            // to 'stateUpdate' and 'gameStarted'. This hook just confirms the join.
            resolve(); // Resolve the promise indicating successful join
          }
        );
      }),
    [socket, isConnected, playerName] // Depend on socket state and player name
  );

  /**
   * createGame
   * Calls the REST endpoint to create the game, then uses the code to join via socket.
   * Resolves with the game code. Navigating should happen after this resolves.
   */
  const createGame = useCallback(
    (isPublic = true): Promise<string> =>
      new Promise(async (resolve, reject) => {
        if (!isAuthenticated) {
             console.warn('Cannot create game: Not authenticated.');
            return reject('Not authenticated');
        }
        if (playerName === null) { // Check explicitly for null
             console.warn('Cannot create game: Player name is required.');
             return reject('Player name is required'); // Should be handled by AuthContext/Login
        }
        if (!socket || !isConnected) {
            console.warn('Cannot create game: Socket not connected.');
             return reject('Socket not connected. Please wait.');
        }

        // Use a temporary ID for the host during creation - backend uses socket.id upon socket join.
        // The hostId sent in the payload is just an identifier for the backend to know *who* created it via REST.
        // The actual hostId in the game state will be the socket ID after the joinGame event.
        const tempHostId = socket.id; // Use the actual socket ID here as it will be the player ID
        console.log(`Attempting to create game (public: ${isPublic}) via REST...`);
        try {
          const response = await fetch('/api/games', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              // Assuming authMiddleware handles authentication via cookie, no need for x-passcode here
            },
            // Backend GameController expects hostId and name to add the first player
            // Pass isPublic flag if the backend POST /games endpoint will use it
            body: JSON.stringify({ hostId: tempHostId, name: playerName /* , isPublic */ }), // isPublic logic needs to be added to backend GameController if used
          });

          if (!response.ok) {
             const errorData = await response.json();
            console.error('Failed to create game via REST:', response.status, errorData);
            return reject(errorData.message || `Failed to create game: ${response.status}`);
          }

          const { gameId, code } = await response.json();
          console.log(`Game created via REST. Code: ${code}. Attempting to join via socket...`);

          // Immediately join the game via socket after creation
          // This will add the player with the correct socket.id and sync state
          // joinGame handles joining the socket room and updating backend state
          joinGame(code) // joinGame now returns void, relies on hook to get state
            .then(() => {
               console.log(`Successfully joined game ${code} via socket after creation.`);
               resolve(code); // Resolve with the code so the caller can navigate
            })
            .catch(socketJoinError => {
                console.error(`Failed to join game ${code} via socket after creation:`, socketJoinError);
                // Decide how to handle this: game created but join failed?
                // Could try to delete the game via another API call? Or let TTL handle it.
                // For now, reject the creation promise.
                reject(`Game created, but failed to join: ${socketJoinError}`);
            });

        } catch (err) {
          console.error('Network or unexpected error during game creation:', err);
          // Type assertion for error
          reject(err instanceof Error ? err.message : 'An unexpected error occurred during game creation.');
        }
      }),
    [isAuthenticated, playerName, socket, isConnected, joinGame] // Depend on auth state, name, socket state, and joinGame
  );

  // FIX: Return the individual error states instead of a combined one
  return { waitingGames, startedGames, createGame, joinGame, isLoadingInitial, error, socketError, refetchGames };
};

export default useLobby;